import { type ComponentProps, useId } from 'react';
import { clsx } from 'clsx';
import { type FieldValues, useController, type UseControllerProps } from 'react-hook-form';

import { Field, FieldError, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';

type FormInputProps<T extends FieldValues> = {
  label: string;
  labelHidden?: boolean;
} & UseControllerProps<T> &
  Omit<ComponentProps<'input'>, 'value' | 'onChange'>;

const FormInput = <T extends FieldValues>({
  label,
  labelHidden,
  className,
  ...props
}: FormInputProps<T>) => {
  const id = useId();

  const {
    field: { ref, value, ...field },
    fieldState: { error },
  } = useController(props);

  return (
    <Field className={className} data-invalid={!!error?.message}>
      <FieldLabel htmlFor={id} className={clsx(labelHidden && 'sr-only')}>
        {label}
      </FieldLabel>
      <Input
        {...props}
        {...field}
        id={id}
        value={value || ''}
        ref={ref}
        aria-invalid={!!error?.message}
      />
      <FieldError>{error?.message}</FieldError>
    </Field>
  );
};

export default FormInput;
